/**
 * HelcimSubscriptionsModal
 * - BasePageWithHeader with transparent toolbar
 * - IonContent + inner IonGrid scroll container
 * - IonFab morphing search overlay
 * - Filter bar: status / plan pill triggers, each opening an anchored
 *   PortalDropdown (same convention as HelcimPaymentsModal.tsx)
 * - Tap card -> HelcimSubscriptionDetailModal
 * - Ellipsis -> shared PortalDropdown (pause / resume / cancel / delete / edit)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PortalDropdown, { PortalDropdownOption } from './PortalDropdown';
import {
  IonModal,
  IonButton,
  IonContent,
  IonIcon,
  IonBadge,
  IonSpinner,
  IonSkeletonText,
  IonText,
  IonSearchbar,
  IonFab,
  IonGrid,
  IonRow,
  IonCol,
  IonCard,
  IonCardContent,
  IonLabel,
  IonRefresherContent,
  IonItem,
} from '@ionic/react';
import {
  searchOutline,
  closeOutline,
  refreshOutline,
  cashOutline,
  calendarOutline,
  refreshCircleOutline,
  chevronDownOutline,
  ellipsisVertical,
  personAddOutline,
  filterOutline,
  pauseCircleOutline,
  playCircleOutline,
  banOutline,
  trashOutline,
  pencil,
} from 'ionicons/icons';
import { Keyboard } from '@capacitor/keyboard';
import { KEYBOARD_GAP_PX } from '../hooks/useKeyboardHeight';
import { getCachedContactList } from '../utils/contactCache';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import type { HelcimSubscription, HelcimCustomer, HelcimPaymentPlan } from '@rsmaids/shared/services/api/helcim/helcim';
import { getSubscriptions, getSubscriptionDetail, getHelcimCustomer, manageSubscription, getPaymentPlans } from '../services/helcimInvoicingService';
import BasePageWithHeader from './BasePageWithHeader';
import PositionedRefresher from './PositionedRefresher';
import HelcimSubscriptionDetailModal from './HelcimSubscriptionDetailModal';
import AddSubscriberModal from './AddSubscriberModal';
import GlassConfirmSheet from './GlassConfirmSheet';
import { applySearchOverlayPosition } from '../utils/overlayPosition';
import styles from './HelcimSubscriptionsModal.module.scss';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../i18n/dynamic';
import { intlLocale } from '../i18n/locale';
import i18n from '../i18n';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  planName?: string;
  paymentPlanId?: number; // when set, loads only subs for this plan
  onSubscriberAdded?: () => void; // called after a new subscriber is added
  onNavigateToContact?: (contactId: string) => void;
}

const STATUS_COLOR: Record<string, string> = {
  active: 'success',
  paused: 'tertiary',
  cancelled: 'medium',
  term_ending: 'warning',
  term_ended: 'medium',
};

// Tone-class lookup for the badge — ion-badge's default (non `.ion-color`)
// :host rule reads --background/--color directly, so a CSS-module tone class
// is applied instead of Ionic's `color` prop, matching InvoiceList's/
// HelcimPaymentsModal's badge system.
const BADGE_TONE_CLASS: Record<string, string> = {
  success: 'badgeSuccess',
  danger: 'badgeDanger',
  warning: 'badgeWarning',
  medium: 'badgeMedium',
  tertiary: 'badgeTertiary',
};

const STATUS_TAB_LABELS: Record<string, string> = {
  get active() { return i18n.t('components.HelcimSubscriptionsModal.active'); },
  get paused() { return i18n.t('components.HelcimSubscriptionsModal.paused'); },
  cancelled: 'Cancelled',
  '': 'All',
};

// PortalDropdown's shared option shape carries `selected`/`role` instead of
// this file's former `value === value` / `destructive` convention — the two
// call sites below now compute `selected` themselves and use `role:
// 'destructive'` where they previously passed `destructive: true`.

const HelcimSubscriptionsModal: React.FC<Props> = ({ isOpen, onClose, title, planName, paymentPlanId, onSubscriberAdded, onNavigateToContact }) => {
  const { t: tr } = useTranslation();
  const [subscriptions, setSubscriptions] = useState<HelcimSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [managing, setManaging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [selectedSub, setSelectedSub] = useState<HelcimSubscription | null>(null);
  const [selectedSubCard, setSelectedSubCard] = useState<Record<string, unknown> | null>(null);
  const [planMap, setPlanMap] = useState<Record<number, string>>({});
  const [allPlans, setAllPlans] = useState<HelcimPaymentPlan[]>([]);
  const [customerObjCache] = useState<Record<string, HelcimCustomer>>({});
  const [planFilter, setPlanFilter] = useState<number | null>(null);
  const [showAddSubscriber, setShowAddSubscriber] = useState(false);
  const [editSub, setEditSub] = useState<HelcimSubscription | null>(null);
  const [pendingDeleteSub, setPendingDeleteSub] = useState<HelcimSubscription | null>(null);

  const [searchText, setSearchText] = useState('');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);

  const [refresherDisabled, setRefresherDisabled] = useState(false);

  const overlayRef = useRef<HTMLElement | null>(null);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
  const scrollRef = useRef<HTMLIonGridElement>(null);
  const ionContentRef = useRef<HTMLIonContentElement>(null);
  const titleCardRef = useRef<HTMLDivElement>(null);
  const wasAtTop = useRef(true);
  const rafTick = useRef(false);
  const enrichedMap = useRef<Map<number, Record<string, unknown>>>(new Map());

  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPlanDropdown, setShowPlanDropdown] = useState(false);
  const statusTriggerRef = useRef<HTMLButtonElement>(null);
  const planTriggerRef = useRef<HTMLButtonElement>(null);
  const pickerPortalRef = useRef<HTMLDivElement>(null);

  // Per-row ellipsis action menu (pause/resume/cancel/delete/edit) — only one
  // open at a time, so a single ref is reassigned to whichever row's trigger
  // was just clicked instead of keeping one ref per row.
  const [openRowMenuSubId, setOpenRowMenuSubId] = useState<number | null>(null);
  const rowMenuTriggerRef = useRef<HTMLIonButtonElement>(null);

  const loadData = useCallback((silent = false) => {
    if (!silent) { setLoading(true); }
    setError(null);
    const subsPromise = paymentPlanId
      ? getSubscriptions({ paymentPlanId, skipEnrichment: true })
      : getSubscriptions({ skipEnrichment: true });

    return subsPromise
      .then(({ subscriptions: subs, plans: subsPlans }) => {
        setSubscriptions(subs);
        // Plan map — use plans returned alongside subscriptions (no extra CF call needed)
        if (subsPlans.length > 0) {
          const map: Record<number, string> = {};
          subsPlans.forEach(p => { if (p.id && p.name) map[p.id] = p.name; });
          setPlanMap(map);
          setAllPlans(subsPlans);
        } else {
          // The skipEnrichment path returns `plans: []` (kept lean since a full
          // plan list isn't needed there), but embeds `paymentPlanName` on each
          // subscription — enough to populate the plan filter's options with
          // whatever plans actually have subscribers, without an extra call.
          const map: Record<number, string> = {};
          subs.forEach(s => {
            const name = s['paymentPlanName'] as string | undefined;
            if (s.paymentPlanId && name) map[s.paymentPlanId] = name;
          });
          if (Object.keys(map).length > 0) setPlanMap(prev => ({ ...map, ...prev }));
        }
        if (planName) {
          const map: Record<number, string> = {};
          subs.forEach(s => { if (s.paymentPlanId) map[s.paymentPlanId] = planName; });
          setPlanMap(prev => ({ ...prev, ...map }));
        }
        enrichedMap.current.clear();
      })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [paymentPlanId, planName]);

  const load = useCallback(() => loadData(false), [loadData]);
  const loadSilent = useCallback(() => loadData(true), [loadData]);

  useEffect(() => {
    if (!isOpen) return;
    // Deferred via a resolved-promise microtask (not called synchronously
    // in the effect body) — setSubscriptions/setError/load's own setState
    // calls are needed on every real invocation (including the many other
    // call sites for load/loadSilent), so they can't just be dropped.
    Promise.resolve().then(() => {
      setSubscriptions([]);
      setError(null);
      load();
    });
  }, [isOpen, load]);

  const customerName = (sub: HelcimSubscription) =>
    (sub['customerName'] as string) ||
    (sub['firstName'] || sub['lastName']
      ? `${sub['firstName'] ?? ''} ${sub['lastName'] ?? ''}`.trim()
      : sub.customerCode ?? '—');

  const filtered = subscriptions.filter(s => {
    const st = (s.status ?? '').toLowerCase();
    const matchStatus = !statusFilter ||
      (statusFilter === 'active' ? ['active', 'term_ending'].includes(st) :
        statusFilter === 'cancelled' ? ['cancelled', 'term_ended'].includes(st) :
          st === statusFilter);
    const q = searchText.toLowerCase().trim();
    const matchSearch = !q || customerName(s).toLowerCase().includes(q) || String(s.id ?? '').includes(q);
    const matchPlan = planFilter === null || s.paymentPlanId === planFilter;
    return matchStatus && matchSearch && matchPlan;
  });

  const counts = {
    active: subscriptions.filter(s => ['active', 'term_ending'].includes((s.status ?? '').toLowerCase())).length,
    paused: subscriptions.filter(s => (s.status ?? '').toLowerCase() === 'paused').length,
    cancelled: subscriptions.filter(s => ['cancelled', 'term_ended'].includes((s.status ?? '').toLowerCase())).length,
  };

  const updateOverlayPosition = useCallback(() => {
    applySearchOverlayPosition(overlayRef.current, isSearchVisible);
  }, [isSearchVisible]);

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  useEffect(() => {
    let showH: { remove: () => Promise<void> } | null = null;
    let hideH: { remove: () => Promise<void> } | null = null;
    let bottomAnimRaf = 0;
    let currentBottom = 0;

    const setBottomOffset = (offset: number) => {
      const el = overlayRef.current;
      if (!el) return;
      if (offset > 0) {
        el.style.setProperty('bottom', `${offset}px`, 'important');
        el.style.removeProperty('transform');
      } else {
        el.style.removeProperty('bottom');
        el.style.removeProperty('transform');
      }
    };

    const animateBottomTo = (h: number) => {
      const targetBottom = h > 0 ? h + KEYBOARD_GAP_PX : 0;
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);
      const startBottom = currentBottom;
      const delta = targetBottom - startBottom;
      if (Math.abs(delta) < 1) {
        currentBottom = targetBottom;
        setBottomOffset(currentBottom);
        return;
      }
      const duration = targetBottom > startBottom ? 220 : 170;
      const startTime = performance.now();
      const step = (now: number) => {
        const t = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        currentBottom = startBottom + (delta * eased);
        setBottomOffset(currentBottom);
        if (t < 1) bottomAnimRaf = requestAnimationFrame(step);
        else {
          currentBottom = targetBottom;
          setBottomOffset(currentBottom);
        }
      };
      bottomAnimRaf = requestAnimationFrame(step);
    };

    const applyKb = (h: number) => {
      animateBottomTo(h);
    };
    try {
      Keyboard.addListener('keyboardWillShow', i => applyKb(i.keyboardHeight || 0)).then(h => { showH = h; }).catch(() => { });
      Keyboard.addListener('keyboardDidShow', i => applyKb(i.keyboardHeight || 0)).catch(() => { });
      Keyboard.addListener('keyboardWillHide', () => applyKb(0)).then(h => { hideH = h; }).catch(() => { });
      Keyboard.addListener('keyboardDidHide', () => applyKb(0)).catch(() => { });
    } catch { }
    const vv = window.visualViewport;
    let raf = 0;
    const onVv = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!vv) return;
        const off = window.innerHeight - vv.height - vv.offsetTop;
        applyKb(off > 50 ? off : 0);
      });
    };
    if (vv) vv.addEventListener('resize', onVv);
    return () => {
      const el = overlayRef.current;
      if (el) { el.style.removeProperty('transform'); el.style.removeProperty('bottom'); }
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);
      if (raf) cancelAnimationFrame(raf);
      if (vv) vv.removeEventListener('resize', onVv);
      try { showH?.remove(); } catch { }
      try { hideH?.remove(); } catch { }
    };
  }, []);

  const handleToggleSearch = () => {
    if (isSearchVisible) {
      setSearchText('');
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => { setIsSearchHidden(true); setIsSearchClosing(false); }, 220);
      try {
        const s = searchRef.current as HTMLIonSearchbarElement | null;
        const g = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
        if (g) g().then(el => { try { el.blur(); } catch { } try { Keyboard.hide(); } catch { } }).catch(() => { try { Keyboard.hide(); } catch { } });
        else { try { Keyboard.hide(); } catch { } }
      } catch { }
    } else {
      setIsSearchHidden(false);
      setIsSearchVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        try {
          const s = searchRef.current as HTMLIonSearchbarElement | null;
          const g = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
          if (g) g().then(el => { try { el.focus(); } catch { } }).catch(() => { });
        } catch { }
      }));
    }
  };

  // getSubscriptions() always passes skipEnrichment, which returns `plans: []` — so
  // allPlans never gets populated from loadData. Fetch the real plan list lazily, the
  // first time the add/edit-subscriber flow is triggered (same pattern used in the
  // desktop SubscriptionsListDesktop), so AddSubscriberModal's plan picker has real
  // options — this header entry point isn't scoped to any one plan up front.
  const ensurePlansLoaded = () => {
    if (allPlans.length > 0) return;
    getPaymentPlans().then(r => setAllPlans(r.plans || [])).catch(() => {});
  };

  const handleManage = async (sub: HelcimSubscription, action: string) => {
    if (!sub.id) return;
    if (action === 'edit') {
      setEditSub(sub);
      ensurePlansLoaded();
      setShowAddSubscriber(true);
      return;
    }
    if (action === 'delete') {
      setPendingDeleteSub(sub);
      return;
    }
    try {
      setManaging(true);
      await manageSubscription(sub.id, action as 'pause' | 'resume' | 'cancel' | 'delete');
      await loadSilent();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setManaging(false);
    }
  };

  // Options for the per-row ellipsis PortalDropdown — same actions as the old
  // CardActionsPopover, just icon + label (no description line — not needed
  // for this compact action list).
  const getRowMenuOptions = (sub: HelcimSubscription): PortalDropdownOption[] => {
    if ((sub.status ?? '').toLowerCase() === 'cancelled') {
      return [
        { value: 'delete', label: tr('components.HelcimSubscriptionsModal.delete'), icon: trashOutline, role: 'destructive' },
      ];
    }
    const isPaused = (sub.status ?? '').toLowerCase() === 'paused';
    return [
      { value: 'edit', label: tr('components.HelcimSubscriptionsModal.editSubscription'), icon: pencil },
      isPaused
        ? { value: 'resume', label: tr('components.HelcimSubscriptionsModal.resume'), icon: playCircleOutline }
        : { value: 'pause', label: tr('components.HelcimSubscriptionsModal.pause'), icon: pauseCircleOutline },
      { value: 'cancel', label: tr('components.HelcimSubscriptionsModal.cancel'), icon: banOutline },
      { value: 'delete', label: tr('components.HelcimSubscriptionsModal.delete'), icon: trashOutline, role: 'destructive' },
    ];
  };

  const openRowMenuSub = subscriptions.find(s => s.id === openRowMenuSubId) ?? null;

  const confirmDelete = async () => {
    if (!pendingDeleteSub?.id) return;
    try {
      await manageSubscription(pendingDeleteSub.id, 'delete');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const fmt = (val?: number) =>
    val != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val) : '—';

  const fmtDate = (d?: string) =>
    d ? new Date(d.includes('T') ? d : d + 'T12:00:00').toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <>
      <IonModal isOpen={isOpen} onDidDismiss={onClose} className={styles.modal}>
        <div className={styles.modalWrapper}>
          {managing && (
            <div className={styles.managingOverlay}>
              <IonSpinner name="bubbles" />
            </div>
          )}
          <BasePageWithHeader
            title={title ?? tr('components.HelcimSubscriptionsModal.subscriptions')}
            toolbarClassName={styles.toolbar}
            smallTitleClassName={styles.smallTitle}
            titleHeaderClassName={styles.titleHeader}
            startButtons={
              <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            }
            endButtons={
              <>
                <IonButton shape="round" className={styles.searchButton} onClick={handleToggleSearch}>
                  <IonIcon icon={isSearchVisible ? closeOutline : searchOutline} />
                </IonButton>
                <IonButton shape="round" className={styles.searchButton} onClick={() => { ensurePlansLoaded(); setShowAddSubscriber(true); }}>
                  <IonIcon icon={personAddOutline} />
                </IonButton>
              </>
            }
          >
            {/* ── Filter bar — pill trigger + portal-anchored glass dropdown card,
                 same convention as HelcimPaymentsModal.tsx's filterBar/periodBtn
                 (replaces the old IonSegment + buried-in-a-menu plan filter). ── */}
            <div className={styles.filterBar}>
              <button
                ref={statusTriggerRef}
                className={styles.periodBtn}
                onClick={() => setShowStatusDropdown(v => !v)}
              >
                {translateDynamic(STATUS_TAB_LABELS[statusFilter] ?? 'All')}
                <IonIcon icon={chevronDownOutline} />
              </button>

              <button
                ref={planTriggerRef}
                className={styles.periodBtn}
                onClick={() => setShowPlanDropdown(v => !v)}
              >
                <IonIcon icon={filterOutline} />
                {planFilter !== null ? (planMap[planFilter] ?? tr('components.HelcimSubscriptionsModal.plan', { planFilter })) : tr('components.HelcimSubscriptionsModal.allPlans')}
                <IonIcon icon={chevronDownOutline} />
              </button>
            </div>

            <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />

            <PortalDropdown
              isOpen={showStatusDropdown}
              anchorRef={statusTriggerRef}
              portalRef={pickerPortalRef}
              onClose={() => setShowStatusDropdown(false)}
              onSelect={(v) => { setStatusFilter(v); setShowStatusDropdown(false); }}
              matchWidth={false}
              options={[
                { value: 'active', label: tr('components.HelcimSubscriptionsModal.active'), sublabel: String(counts.active), selected: statusFilter === 'active' },
                { value: 'paused', label: tr('components.HelcimSubscriptionsModal.paused'), sublabel: String(counts.paused), selected: statusFilter === 'paused' },
                { value: 'cancelled', label: tr('components.HelcimSubscriptionsModal.cancelled'), sublabel: String(counts.cancelled), selected: statusFilter === 'cancelled' },
                { value: '', label: tr('components.HelcimSubscriptionsModal.all'), selected: statusFilter === '' },
              ]}
            />

            <PortalDropdown
              isOpen={showPlanDropdown}
              anchorRef={planTriggerRef}
              portalRef={pickerPortalRef}
              onClose={() => setShowPlanDropdown(false)}
              onSelect={(v) => { setPlanFilter(v === '' ? null : Number(v)); setShowPlanDropdown(false); }}
              align="start"
              matchWidth={false}
              options={[
                { value: '', label: tr('components.HelcimSubscriptionsModal.allPlans'), selected: planFilter === null },
                // Custom one-off plans created per-invoice (e.g. via
                // createCustomPricePlan) get named after their invoice number
                // instead of a real plan name — not useful to filter by, so
                // they're excluded here.
                ...Object.entries(planMap)
                  .filter(([, name]) => !/^INV[\d_]+$/i.test(name))
                  .map(([id, name]) => ({
                    value: id,
                    label: name,
                    sublabel: String(subscriptions.filter(s => s.paymentPlanId === Number(id)).length),
                    selected: planFilter === Number(id),
                  })),
              ]}
            />

            {/* Per-row ellipsis action menu (pause/resume/cancel/delete/edit) —
                one shared dropdown, retargeted to whichever row's trigger was
                last clicked, instead of one CardActionsPopover per row. */}
            <PortalDropdown
              isOpen={openRowMenuSubId !== null}
              anchorRef={rowMenuTriggerRef}
              portalRef={pickerPortalRef}
              onClose={() => setOpenRowMenuSubId(null)}
              onSelect={(v) => {
                setOpenRowMenuSubId(null);
                if (openRowMenuSub) handleManage(openRowMenuSub, v);
              }}
              align="end"
              options={openRowMenuSub ? getRowMenuOptions(openRowMenuSub) : []}
            />

            {/* ── IonContent + inner IonGrid scroll container ── */}
            <IonContent ref={ionContentRef} className={styles.content}>
              <PositionedRefresher
                titleRef={titleCardRef}
                contentRef={scrollRef}
                offset={0}
                anchor="bottom"
                id="subs-refresher"
                pullMin={120}
                pullMax={200}
                pullFactor={0.6}
                disabled={refresherDisabled}
                onIonRefresh={(e) => {
                  load()?.finally(() => e.detail.complete());
                }}
                style={{
                  '--ion-color-step-250': 'var(--ion-background-color)',
                  '--ion-color-step-200': 'var(--ion-background-color)',
                  '--ion-background-color': 'transparent',
                } as React.CSSProperties}
              >
                <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
              </PositionedRefresher>

              <IonGrid
                ref={scrollRef}
                className={styles.subsContainer}
                onScroll={(e) => {
                  const top = (e.target as HTMLElement).scrollTop || 0;
                  const atTop = top <= 5;
                  if (atTop !== wasAtTop.current) {
                    wasAtTop.current = atTop;
                    setRefresherDisabled(!atTop);
                  }
                  if (!rafTick.current) {
                    rafTick.current = true;
                    requestAnimationFrame(() => { rafTick.current = false; });
                  }
                }}
              >
                {/* Anchor for PositionedRefresher — 0-height, invisible */}
                <div ref={titleCardRef} />

                {loading && [0, 1, 2, 3, 4 ].map(i => (
                  <IonCard key={`skeleton-${i}`} className={`${styles.card} ${styles.cardActive}`} aria-hidden="true">
                    <IonCardContent className={styles.cardBody}>
                      <IonItem lines="none" className={styles.cardTop}>
                        <IonLabel>
                          <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '50%', height: 16, margin: 0 }} />
                        </IonLabel>
                        <IonSkeletonText animated slot="end" className={styles.skeletonBar} style={{ width: 64, height: 22, margin: 0, borderRadius: 11 }} />
                      </IonItem>
                      <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '40%', height: 13, margin: '4px 0 12px' }} />
                      <IonGrid className={styles.statsRow}>
                        <IonRow>
                          {[0, 1, 2].map(j => (
                            <IonCol key={j} className={styles.stat}>
                              <IonSkeletonText animated className={styles.skeletonBar} style={{ width: 20, height: 20, margin: 0, borderRadius: 5 }} />
                              <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '65%', height: 14, margin: '4px 0 0' }} />
                              <IonSkeletonText animated className={styles.skeletonBar} style={{ width: '45%', height: 11, margin: '3px 0 0' }} />
                            </IonCol>
                          ))}
                        </IonRow>
                      </IonGrid>
                    </IonCardContent>
                  </IonCard>
                ))}

                {!loading && error && (
                  <div className={styles.center}>
                    <IonText color="danger">{error}</IonText>
                    <IonButton fill="clear" onClick={load}>
                      <IonIcon slot="start" icon={refreshOutline} />{tr('components.HelcimSubscriptionsModal.retry')}
                    </IonButton>
                  </div>
                )}

                {!loading && !error && filtered.length === 0 && (
                  <div className={styles.center}>
                    <IonText color="medium">{tr('components.HelcimSubscriptionsModal.noSubscriptionsFound')}</IonText>
                  </div>
                )}

                {!loading && !error && filtered.length > 0 && filtered.map(sub => {
                  const status = (sub.status ?? 'unknown').toLowerCase();
                  return (
                    <IonCard
                      key={sub.id}
                      className={`${styles.card} ${['active', 'term_ending'].includes(status) ? styles.cardActive : styles.cardInactive}`}
                      onClick={(e: React.MouseEvent) => {
                        // composedPath() sees the real event path through
                        // ion-button's (open) shadow root, unlike e.target
                        // which shadow-DOM retargeting can obscure — this
                        // reliably detects the click originated in the row
                        // menu button no matter which phase/order Ionic's
                        // own click wiring fires in.
                        const path = e.nativeEvent.composedPath();
                        if (path.some(el => el instanceof HTMLElement && el.hasAttribute('data-row-menu-btn'))) {
                          return;
                        }
                        const isBank = sub.paymentMethod === 'bank';
                        const pickPayment = (customer: Record<string, unknown>) => {
                          if (isBank) {
                            const banks = (customer['bankAccounts'] as Record<string, unknown>[] | undefined) ?? [];
                            const bank = banks.find(b => b['isDefault'] === 1 || b['isDefault'] === true) ?? banks[0] ?? null;
                            return bank ? { ...bank, _type: 'bank' } : null;
                          }
                          const cards = (customer['cards'] as Record<string, unknown>[] | undefined) ?? [];
                          return cards.find(c => c['isDefault'] === 1 || c['isDefault'] === true) ?? cards[0] ?? null;
                        };
                        const cached = enrichedMap.current.get(sub.id);
                        if (cached?.['_customerLoaded']) {
                          setSelectedSubCard(pickPayment(cached));
                          setSelectedSub({ ...sub, ...cached } as HelcimSubscription);
                        } else {
                          setSelectedSub(sub);
                          if (sub.customerCode) {
                            getHelcimCustomer(sub.customerCode, sub['helcimCustomerId'] as number | undefined).then((customer: Record<string, unknown> | null) => {
                              if (!customer) return;
                              const card = pickPayment(customer);
                              setSelectedSubCard(card);
                              const existing = enrichedMap.current.get(sub.id) ?? {};
                              enrichedMap.current.set(sub.id, { ...existing, ...customer, _customerLoaded: true });
                              setSelectedSub(prev => prev?.id === sub.id ? { ...prev } : prev);
                            }).catch(() => {});
                          }
                          // Subscription detail for payment history in parallel
                          getSubscriptionDetail(sub.id, { paymentPlanId: sub.paymentPlanId ?? undefined, recurringAmount: sub.recurringAmount ?? undefined }).then(enriched => {
                            if (enriched) {
                              const existing = enrichedMap.current.get(sub.id) ?? {};
                              enrichedMap.current.set(sub.id, { ...existing, ...enriched as Record<string, unknown> });
                              setSelectedSub(prev => prev?.id === sub.id ? { ...sub, ...enriched } as HelcimSubscription : prev);
                            }
                          }).catch(() => {});
                        }
                      }}
                    >
                      <IonCardContent className={styles.cardBody}>
                        <IonItem lines="none" className={styles.cardTop}>
                          <IonLabel className={styles.customerBlock}>
                            <h2 className={styles.cardName}>{customerName(sub)}</h2>
                          </IonLabel>
                          <IonBadge slot="end" className={`${styles.badge} ${styles[BADGE_TONE_CLASS[STATUS_COLOR[status] ?? 'medium']]}`}>
                            {{ term_ending: 'Term Ending', term_ended: 'Term Ended' }[status] ?? (sub.status ?? 'unknown')}
                          </IonBadge>
                          <IonButton
                            fill="clear"
                            slot="end"
                            className={styles.cardMenuBtn}
                            data-row-menu-btn="true"
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              rowMenuTriggerRef.current = e.currentTarget;
                              setOpenRowMenuSubId(sub.id ?? null);
                            }}
                          >
                            <IonIcon icon={ellipsisVertical} slot="icon-only" className={styles.cardMenuIcon} />
                          </IonButton>
                        </IonItem>
                        <p className={styles.planName}>
                          {(sub['paymentPlanName'] as string | undefined)
                            || (sub.paymentPlanId && planMap[sub.paymentPlanId])
                            || (sub.paymentPlanId ? `#${sub.paymentPlanId}` : '—')}
                        </p>

                        <IonGrid className={styles.statsRow}>
                          <IonRow>
                            <IonCol className={styles.stat}>
                              <IonIcon icon={refreshCircleOutline} className={`${styles.statIcon} ${styles.statIconBlue}`} />
                              <IonText className={styles.statValue}>
                                {sub.timesBilled ?? 0}/{sub.maxCycles === 0 || sub.maxCycles == null ? '∞' : sub.maxCycles}
                              </IonText>
                              <IonText className={styles.statLabel}>{tr('components.HelcimSubscriptionsModal.cycle')}</IonText>
                            </IonCol>
                            <IonCol className={styles.stat}>
                              <IonIcon icon={cashOutline} className={`${styles.statIcon} ${styles.statIconGreen}`} />
                              <IonText className={styles.statValue}>{fmt((sub['totalAmount'] as number | undefined) ?? sub.recurringAmount)}</IonText>
                              <IonText className={styles.statLabel}>{tr('components.HelcimSubscriptionsModal.nextPayment')}</IonText>
                            </IonCol>
                            <IonCol className={styles.stat}>
                              <IonIcon icon={calendarOutline} className={`${styles.statIcon} ${styles.statIconPurple}`} />
                              <IonText className={styles.statValue}>{fmtDate(sub.dateBilling)}</IonText>
                              <IonText className={styles.statLabel}>{tr('components.HelcimSubscriptionsModal.nextBilling')}</IonText>
                            </IonCol>
                          </IonRow>
                        </IonGrid>
                      </IonCardContent>
                    </IonCard>
                  );
                })}

                {isSearchVisible && <div className={styles.searchBarSpacer} />}
              </IonGrid>
            </IonContent>

            {/* Morphing search overlay — outside IonContent, inside BasePageWithHeader */}
            <IonFab
              ref={el => { overlayRef.current = el; }}
              className={`${styles.searchMorph} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`}
              aria-hidden={!isSearchVisible}
            >
              <IonSearchbar
                ref={searchRef}
                value={searchText}
                debounce={50}
                onIonInput={e => setSearchText(e.detail.value ?? '')}
                onIonClear={() => setSearchText('')}
                placeholder={tr('components.HelcimSubscriptionsModal.searchSubscriptions', { length: subscriptions.length })}
                animated
                showCancelButton="never"
                showClearButton={searchText ? 'always' : 'never'}
                className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
              />
            </IonFab>

          </BasePageWithHeader>

        <GlassConfirmSheet
          isOpen={!!pendingDeleteSub}
          title={tr('components.HelcimSubscriptionsModal.deleteSubscription')}
          message={tr('components.HelcimSubscriptionsModal.areYouSureYouWantTo')}
          confirmLabel="Delete Subscription"
          cancelLabel="No, keep it"
          variant="danger"
          onConfirm={confirmDelete}
          onDismiss={() => setPendingDeleteSub(null)}
        />
        </div>
      </IonModal>

      <HelcimSubscriptionDetailModal
        subscription={selectedSub}
        isOpen={!!selectedSub}
        onClose={() => { setSelectedSub(null); setSelectedSubCard(null); }}
        planName={
          (selectedSub?.['paymentPlanName'] as string | undefined)
          || (selectedSub?.paymentPlanId ? planMap[selectedSub.paymentPlanId] : undefined)
        }
        customerName={selectedSub ? customerName(selectedSub) : undefined}
        cardData={selectedSubCard}
        onContactClick={onNavigateToContact ? async () => {
          const email = (customerObjCache[selectedSub?.customerCode ?? ''] as HelcimCustomer & { billingAddress?: { email?: string } })?.billingAddress?.email;
          if (!email) return;
          const svc = DataServiceFactory.getService();
          const { data: contacts } = await getCachedContactList(() => svc.contacts.getAllContacts());
          const match = contacts.find(c => c.primaryEmail?.toLowerCase() === email.toLowerCase());
          if (!match?.id) return;
          setSelectedSub(null);
          setSelectedSubCard(null);
          onClose();
          onNavigateToContact(match.id);
        } : undefined}
        onManage={(sub, action) => handleManage(sub, action)}
      />

      {showAddSubscriber && (
        <AddSubscriberModal
          isOpen={showAddSubscriber}
          plan={allPlans.find(p => p.id === (editSub?.paymentPlanId ?? planFilter ?? paymentPlanId))}
          allPlans={allPlans}
          editSubscription={editSub}
          editCustomer={editSub?.customerCode ? customerObjCache[editSub.customerCode] ?? null : null}
          onClose={() => { setShowAddSubscriber(false); setEditSub(null); }}
          onSubscriberAdded={() => { setShowAddSubscriber(false); setEditSub(null); load(); onSubscriberAdded?.(); }}
        />
      )}

    </>
  );
};

export default HelcimSubscriptionsModal;
